import { Routes } from '@angular/router';
import { OktaCallbackComponent } from '@okta/okta-angular';
import { AuthGuard } from './core/guards/auth.guard';
import { dashboardRoute } from 'src/app/core/core';
import { SelectedDeviceGuard } from './shared/device/selected-device.guard';
import { SiteConfigResolver } from './core/resolvers';
import { PageResourcesResolver } from './core/resolvers';
import { LoginComponent } from './modules/auth/components/login/login.component';
import { ServiceRequestExitGuard } from './core/guards/service-request-exit.guard';
import { HomeGuard } from './core/guards/home.guard';
import { ServiceRequestActivateGuard } from './core/guards/service-request-activate.guard';
import { LoginDisabledGuard } from './core/guards/login-disabled.guard';
import { LookupAccountActivateGuard } from './core/guards/lookup-account-activate.guard';
import { PlanCoverageRedirectGuard } from './core/guards/plan-coverage-redirect.guard';
import { PlanCoverageProductDetailGuard } from './core/guards/plan-coverage-product-detail.guard';
import { PlanRenewalActivateGuard } from './core/guards/plan-renewal-activate.guard';
import { CmsEditorActivateGuard } from './core/guards/cms-editor-activate.guard';
import { PrivacyPolicyRedirectGuard, TermsOfUseRedirectGuard } from './core/guards/footer-link-redirect.guard';
import { PlanRenewalService } from './shared/components/plan-renewal-notice';

export const routes: Routes = [
  {
    path: '',
    data: { loadGlobal: true },
    resolve: { resources: SiteConfigResolver },
    children: [
      {
        path: '',
        data: { title: '', sfresources: 'PlanCoverage,PlanCoverageMapp,PlanCoverageLpp,PlanCoverageProductDetail,CertificateItemInfo,ContactUs,PlanRenewal,Dashboard,Global' },
        resolve: { resources: PageResourcesResolver },
        runGuardsAndResolvers: 'always',
        children: [
          { path: '', redirectTo: `/${dashboardRoute}`, pathMatch: 'full' },
          {
            path: dashboardRoute,
            loadComponent: () => import('./modules/dashboard/components/dashboard.component').then((m) => m.DashboardComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: 'DashboardPage',
              title: 'Dashboard',
              sfresources: 'Global,Modal,Chat,Dashboard,Header,Addons,FAQ,MyPlan,Payment,ServiceRequest,CertificateItemInfo',
            },
          },
          {
            path: 'my-plan',
            canActivate: [AuthGuard],
            children: [
              {
                path: '',
                loadComponent: () => import('./modules/my-plan/my-plan.exports').then((m) => m.MyPlanComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'My Plan',
                  sfresources: 'Global,Modal,Chat,Dashboard,Header,Addons,FAQ,MyPlan,Payment,CancelPlan',
                },
              },
              {
                path: 'payment',
                loadComponent: () => import('./modules/my-plan/my-plan.exports').then((m) => m.MyPlanPaymentComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'My Plan Payment',
                  sfresources: 'Global,Modal,Chat,Dashboard,Header,Addons,FAQ,MyPlan,Payment',
                },
              },
            ],
          },
          {
            path: 'cancellation',
            canActivate: [AuthGuard],
            resolve: { resources: PageResourcesResolver },
            data: { sfresources: 'Global,Modal,CancelPlan,MyPlan,Signup,AddressEntry' },
            children: [
              {
                path: '',
                loadComponent: () => import('./modules/cancellation/cancellation.exports').then((m) => m.CancelPlanFlowComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Cancel Plan',
                  sfresources: 'Global,Modal,CancelPlan,MyPlan,Signup,AddressEntry',
                },
              },
              {
                // Kept as a standalone fallback route (e.g. deep links from before this PBI);
                // the primary entry point now goes through the '' flow container above.
                path: 'review',
                loadComponent: () => import('./modules/cancellation/cancellation.exports').then((m) => m.ReviewCancellationComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Cancel Plan Review',
                  sfresources: 'Global,Modal,CancelPlan,MyPlan,Signup',
                },
              },
            ],
          },
          {
            path: 'support',
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            children: [
              {
                path: '',
                loadComponent: () => import('./modules/support/support.exports').then((m) => m.SupportComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Support',
                  sfresources: 'Global,Modal,Support,FAQ',
                },
              },
              {
                path: 'guides',
                loadComponent: () => import('./modules/support/support.exports').then((m) => m.GuidesComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Home Guides',
                  sfresources: 'Global,Modal,Support',
                },
              },
            ],
          },
          {
            path: 'account',
            loadComponent: () => import('./modules/account/account.exports').then((m) => m.AccountComponent),
            resolve: { resources: PageResourcesResolver },
            data: {},
            canActivate: [AuthGuard],
            children: [
              { path: '', redirectTo: 'profile', pathMatch: 'full' },
              {
                path: 'profile',
                loadComponent: () => import('./modules/account/account.exports').then((m) => m.AccountProfileComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Profile',
                  sfresources: 'Global,Modal,Account,PasswordForm,Payment,Billing',
                },
              },
              {
                path: 'change-password',
                loadComponent: () => import('./shared/shared.exports').then((m) => m.PasswordSettingsComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Change Password',
                  sfresources: 'Global,Modal,Account,PasswordForm,Payment,Billing',
                },
              },
              {
                path: 'manage-payments',
                loadComponent: () => import('./modules/account/account.exports').then((m) => m.ManagePaymentsComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Manage Payments',
                  sfresources: 'Global,Modal,Account,PasswordForm,Payment,Billing',
                },
              },
              {
                path: 'preferences',
                loadComponent: () => import('./modules/account/account.exports').then((m) => m.PreferenceCenterComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Preferences',
                  sfresources: 'Global,Modal,Account',
                },
              },
              {
                path: 'billing',
                loadComponent: () => import('./modules/account/account.exports').then((m) => m.BillingComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Billing',
                  sfresources: 'Global,Modal,Account,PasswordForm,Payment,Billing,MyPlan',
                },
              },
              {
                path: 'subscription',
                loadComponent: () => import('./modules/account/account.exports').then((m) => m.SubscriptionComponent),
                resolve: { resources: PageResourcesResolver },
                data: {
                  component: '',
                  title: 'Subscription',
                  sfresources: 'Global,Modal,Account,PasswordForm,Payment,Billing',
                },
              },
            ],
          },
          { path: 'payment-info', redirectTo: '/account/payment-info' },
          { path: 'tech-support-history', redirectTo: '/account/tech-support-history' },
          { path: 'account/claims', redirectTo: '/claims' },
          { path: 'file-a-claim', redirectTo: '/claims' },
          {
            path: 'tech-services',
            loadComponent: () => import('./modules/tech-services/tech-services.exports').then((m) => m.TechServicesComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Tech Services',
              sfresources: 'Global,Modal,Chat,TechServices,Tutorial',
            },
          },

          {
            path: 'coverage',
            loadComponent: () => import('./modules/coverage/coverage.exports').then((m) => m.CoverageComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Coverage',
              sfresources: 'Global,Modal,Chat,Coverage,Tutorial',
            },
          },
          {
            path: 'coverage-mobile',
            loadComponent: () => import('./modules/coverage/coverage.exports').then((m) => m.CoverageMobileComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Coverage - Protection 360™',
              sfresources: 'Global,Modal,Chat,Coverage,Tutorial',
            },
          },
          {
            path: 'coverage-home',
            loadComponent: () => import('./modules/coverage/coverage.exports').then((m) => m.CoverageHomeComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Coverage - Protection 360™ HomeTech',
              sfresources: 'Global,Modal,Chat,Coverage,Tutorial,Dashboard',
            },
          },
          {
            path: 'plan-coverage',
            canActivate: [AuthGuard, PlanCoverageRedirectGuard],
            loadComponent: () => import('./modules/plan-coverage/plan-coverage.exports').then((m) => m.PlanCoverageComponent),
            resolve: { resources: PageResourcesResolver },
            data: {
              component: '',
              title: 'Plan & Coverage',
              sfresources: 'Global,Modal,Chat,Dashboard,Header,MyPlan,PlanCoverage',
            },
          },
          {
            path: 'plan-coverage-mapp',
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./modules/plan-coverage-mapp/plan-coverage-mapp.exports').then(
                (m) => m.PlanCoverageMappComponent
              ),
            resolve: { resources: PageResourcesResolver },
            data: {
              component: '',
              title: 'Plan & Coverage - MAPP',
              sfresources: 'Global,Modal,Chat,Dashboard,Header,MyPlan,PlanCoverage,PlanCoverageMapp,CancelPlan,Account',
            },
          },
          {
            path: 'plan-coverage-single-cert-policy',
            canActivate: [AuthGuard],
            loadComponent: () =>
              import('./modules/plan-coverage-single-cert-policy/plan-coverage-single-cert-policy.exports').then(
                (m) => m.PlanCoverageSingleCertPolicyComponent
              ),
            resolve: { resources: PageResourcesResolver },
            data: {
              component: '',
              title: "Plan & Coverage - Lowe's Protection Plan",
              sfresources: 'Global,Modal,Chat,Dashboard,Header,MyPlan,PlanCoverage,PlanCoverageLpp,CancelPlan',
            },
          },
          {
            path: 'plan-coverage-product-detail',
            canActivate: [AuthGuard, PlanCoverageProductDetailGuard],
            loadComponent: () =>
              import('./modules/plan-coverage-product-detail/plan-coverage-product-detail.exports').then(
                (m) => m.PlanCoverageProductDetailComponent
              ),
            resolve: { resources: PageResourcesResolver },
            data: {
              component: '',
              title: 'Plan & Coverage - Product Detail',
              sfresources: 'Global,Modal,Chat,Dashboard,Header,MyPlan,PlanCoverage,PlanCoverageMapp,PlanCoverageLpp,PlanCoverageProductDetail,CertificateItemInfo,PlanDetails',
            },
          },
          {
            path: 'claims-support',
            loadComponent: () => import('./modules/claims/components/claims-support/claims-support.component').then((m) => m.ClaimsSupportComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Support Center',
              sfresources: 'Global,Modal,Chat,Claims,ClaimsSupportCenter,FAQ,Tutorial',
            },
          },
          {
            path: 'my-devices',
            loadComponent: () => import('./modules/home-devices/home-devices.exports').then((m) => m.DeviceInventoryComponentV3),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'My Devices',
              sfresources:
                'Global,Modal,Chat,Devices,DeviceInventory,DeviceSearch,DeviceCard,DeviceDetails,RegisterTablet,RegisterWatch,RegisterSmartwatch,RegisterCellularDevice,RegisterNonCellularDevice,Tutorial',
            },
          },
          {
            path: 'home-device-details',
            loadComponent: () => import('./modules/home-devices/home-devices.exports').then((m) => m.HomeDeviceDetailsComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard, SelectedDeviceGuard],
            data: {
              component: '',
              title: 'Home Device Details',
              sfresources: 'Global,Modal,Chat,DeviceDetails,Devices,RegisterTablet,RegisterWatch,RegisterSmartwatch,RegisterCellularDevice,RegisterNonCellularDevice,CBR,Tutorial',
            },
          },
          {
            path: 'device-select',
            loadComponent: () => import('./modules/account/account.exports').then((m) => m.ClaimsSelectDeviceComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'Claims Device Select',
              sfresources: 'Global,Modal,Chat,ClaimsSelectDevice,DeviceInventory,DeviceCard,Tutorial',
            },
          },
          {
            path: 'faq',
            loadComponent: () => import('./shared/shared.exports').then((m) => m.FaqComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'FAQ',
              sfresources: 'Global,Modal,Chat,FAQ,Tutorial',
            },
          },
          {
            path: 'p360-faq',
            loadComponent: () => import('./shared/shared.exports').then((m) => m.FaqP360Component),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: {
              component: '',
              title: 'FAQ P360',
              sfresources: 'Global,Modal,Chat,FAQ,Tutorial',
            },
          },

          {
            path: 'live-help',
            loadComponent: () => import('./modules/live-help/live-help.component').then((m) => m.LiveHelpComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard],
            data: { component: '', title: 'Live Help', sfresources: 'Global,Modal,Chat,LiveHelp' },
          },
          {
            path: 'service-request',
            loadComponent: () => import('./modules/service-request/components/service-request/service-request.component').then((m) => m.ServiceRequestComponent),
            resolve: { resources: PageResourcesResolver },
            providers: [PlanRenewalService],
            canActivate: [AuthGuard, ServiceRequestActivateGuard],
            canDeactivate: [ServiceRequestExitGuard],
            data: {
              component: '',
              title: 'Service Request',
              sfresources: 'Global,Modal,ServiceRequest,Devices,DeviceInventory,DeviceSearch,DeviceCard,DeviceDetails,Tutorial,ApplianceDetails,MyPlan,CertificateItemInfo,Signup,ProductServiceAddress',
            },
          },
          {
            path: 'select-home',
            loadComponent: () => import('./modules/select-home/select-home.component').then((m) => m.SelectHomeComponent),
            resolve: { resources: PageResourcesResolver },
            canActivate: [AuthGuard, HomeGuard],
            data: { component: '', title: 'Choose your home', sfresources: 'Global,SelectHome' },
          },
          {
            path: 'renew',
            loadComponent: () => import('./modules/plan-renewal/plan-renewal.component').then((m) => m.PlanRenewalComponent),
            resolve: { resources: PageResourcesResolver },
            providers: [PlanRenewalService],
            canActivate: [AuthGuard, PlanRenewalActivateGuard],
            data: {
              component: '',
              title: 'Renew Your Plan',
              sfresources: 'Global,Modal,Dashboard,PlanRenewal',
            },
          },
        ],
      },

      {
        path: 'home',
        loadComponent: () => import('./modules/home/home.exports').then(m => m.HomeComponent),
        resolve: { resources: PageResourcesResolver },
        data: { component: '', title: 'Home', sfresources: 'Global,Home' },
      },
      {
        path: 'faqs',
        loadComponent: () => import('./modules/faqs/faqs.component').then(m => m.FaqsComponent),
        resolve: { resources: PageResourcesResolver },
        data: { component: '', title: 'FAQs', sfresources: 'Global,FAQs' },
      },
      {
        path: 'terms-and-conditions',
        loadComponent: () =>
          import('./modules/terms-and-conditions/terms-and-conditions.component').then(
            (m) => m.TermsAndConditionsComponent
          ),
        resolve: { resources: PageResourcesResolver },
        data: { component: '', title: 'Terms and Conditions', sfresources: 'Global,Header,MyPlan,TermsConditions' },
      },
      {
        path: 'login',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: LoginComponent,
          title: '',
          sfresources: 'Global,Modal,Chat,Home,Login,SignUp',
        },
        children: [
          { path: '', component: LoginComponent, canActivate: [LoginDisabledGuard], data: { title: 'Login' } },
          { path: 'callback', component: OktaCallbackComponent, data: { title: '' } },
        ],
      },
      { path: 'sso', loadComponent: () => import('./modules/auth/auth.exports').then((m) => m.OktaHostedLoginComponent), data: { title: 'Login SSO' } },
      {
        path: 'createsession',
        loadComponent: () => import('./modules/auth/components/create-session/create-session.component').then((m) => m.CreateSessionComponent),
        resolve: { resources: PageResourcesResolver },
        data: { title: 'Create Session', sfresources: 'Global,TechSupport,MyPlan' },
      },
      {
        path: 'create-session',
        loadComponent: () => import('./modules/auth/components/create-session/create-session.component').then((m) => m.CreateSessionComponent),
        resolve: { resources: PageResourcesResolver },
        data: { title: 'Create Session', sfresources: 'Global,TechSupport,MyPlan' },
      },
      { path: 'logout', loadComponent: () => import('./modules/auth/auth.exports').then((m) => m.LogoutComponent), canActivate: [AuthGuard], data: { title: 'Logout' } },

      {
        path: 'signin/forgot-password',
        loadComponent: () => import('./modules/auth/auth.exports').then((m) => m.LoginComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Forgot Password',
          sfresources: 'Global,Modal,Chat,Home,Login,SignUp',
        },
      },
      {
        path: 'create-account',
        canActivate: [LoginDisabledGuard],
        loadComponent: () => import('./modules/onboarding/onboarding.exports').then((m) => m.CreateAccountComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          title: 'Onboarding',
          sfresources: 'Global,Modal,Chat,Signup,PasswordForm,LookupUser,LookupPlan,URL,SPOE,Tutorial',
        },
      },
      {
        path: 'lookup-account',
        canActivate: [LookupAccountActivateGuard],
        loadComponent: () => import('./modules/onboarding/onboarding.exports').then((m) => m.CreateAccountComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          title: 'Account Lookup',
          sfresources: 'Global,Modal,Chat,Signup,PasswordForm,LookupUser,LookupPlan,URL,SPOE,Tutorial',
        },
      },
      {
        path: 'invite-expired',
        loadComponent: () => import('./modules/onboarding/onboarding.exports').then((m) => m.InviteExpiredComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Onboarding Invite Expired',
          sfresources: 'Global,Modal,Chat,SignUp,Tutorial',
        },
      },
      {
        path: 'accept-invite',
        loadComponent: () => import('./modules/onboarding/onboarding.exports').then((m) => m.AcceptInviteComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Onboarding Accept Invite',
          sfresources: 'Global,Modal,Chat,SignUp,Tutorial',
        },
      },
      {
        path: 'signup-invite',
        loadComponent: () => import('./modules/onboarding/onboarding.exports').then((m) => m.SignupInviteComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Onboarding Signup Invite',
          sfresources: 'Global,Modal,Chat,SignUp,PasswordForm,Tutorial,LookupUser',
        },
      },
      {
        path: 'tech-support',
        loadComponent: () => import('./modules/tech-support/tech-support.component').then((m) => m.TechSupportComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Tech Support',
          sfresources: 'Global,Modal,Chat,Devices,TechSupport,Tutorial',
        },
      },
      {
        path: 'page/:key',
        loadComponent: () => import('./modules/generic-content-page/generic-content-page.component').then((m) => m.GenericContentPageComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: '',
          sfresources: 'Global,Modal,Chat,GenericContent',
        },
      },
      {
        path: 'contact-us',
        loadComponent: () => import('./modules/contact-us/contact-us.exports').then((m) => m.ContactUsComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Contact Us',
          sfresources: 'Global,Modal,Chat,ContactUs,Support',
        },
      },
      {
        path: 'under-maintenance',
        loadComponent: () => import('./modules/under-maintenance/under-maintenance.component').then((m) => m.UnderMaintenanceComponent),
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Under Maintenance',
          sfresources: 'Global,Modal,Chat,Devices,UnderMaintenance,TechSupport,Tutorial',
        },
      },
      {
        path: 'service_fee_deductibles',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Service Fee / Deductibles',
          sfresources: 'Global,Deductible,Tutorial',
        },
        children: [{ path: '', loadComponent: () => import('./modules/deductible-lookup/components/deductible-lookup.component').then((m) => m.DeductibleLookupComponent) }],
      },
      {
        path: 'cms',
        canActivate: [CmsEditorActivateGuard],
        resolve: { resources: PageResourcesResolver },
        data: { component: '', title: 'CMS Page', sfresources: '_' },
        children: [{ path: '', loadComponent: () => import('./modules/cms/cms.exports').then((m) => m.CMSComponent) }],
      },
      {
        path: 'document/:filename',
        loadComponent: () => import('./modules/document/components/doument.component').then((m) => m.DocumentComponent),
        resolve: { resources: PageResourcesResolver },
        data: { component: '', title: 'Document', sfresources: 'Global,Document' },
      },
      {
        path: 'documents/:filename',
        loadComponent: () => import('./modules/document/components/doument.component').then((m) => m.DocumentComponent),
        resolve: { resources: PageResourcesResolver },
        data: { component: '', title: 'Document', sfresources: 'Global,Document' },
      },
      {
        path: 'editor',
        resolve: { resources: PageResourcesResolver },
        data: {
          component: '',
          title: 'Editor',
          sfresources: 'Global,Modal,Editor,Tutorial',
        },
        children: [{ path: '', loadComponent: () => import('./modules/cms/cms.exports').then((m) => m.EditorComponent) }],
      },
    ],
  },
  { path: 'health-check', loadComponent: () => import('./modules/health-check/health-check-page/health-check-page.component').then((m) => m.HealthCheckPageComponent) },
  /* LOWES Legacy links redirects */
  { path: 'pages/term-and-conditions', redirectTo: '/terms-and-conditions' },
  { path: 'pages/terms-and-conditions', redirectTo: '/terms-and-conditions' },
  { path: 'pages/faq', redirectTo: '/faqs' },
  { path: 'pages/contact-us', redirectTo: '/contact-us' },
  { path: 'register-plan', redirectTo: '/lookup-account' },
  { path: "claims-and-support/overview/faq's", redirectTo: '/faqs' },
  { path: 'privacypolicy', canActivate: [PrivacyPolicyRedirectGuard], children: [] },
  { path: 'pages/terms-of-use', canActivate: [TermsOfUseRedirectGuard], children: [] },
  { path: '**', redirectTo: '/' },
];
